import { appAlert } from '@/lib/appAlert';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { markNotificationsReadForRoute } from '@/lib/notifications';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Linking,
  Modal,
  PanResponder,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  Circle,
  ClipPath,
  Defs,
  G,
  Image as SvgImage,
  Line,
  Rect,
  Svg,
  Text as SvgText,
} from 'react-native-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { contentMaxWidth, fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useAuth } from '@/contexts/AuthContext';
import { useNeedsAction } from '@/contexts/NeedsActionContext';
import { useProfile } from '@/contexts/ProfileContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  getMutualConnections,
  listMyConnections,
  listPendingReceived,
  respondConnectionRequest,
  type ConnectionWithProfile,
  type MutualPair,
  type PendingConnection,
} from '@/lib/connections';
import { getActiveAgreementPartnerIds } from '@/lib/agreements';
import { getCircleAnnouncements, type Announcement } from '@/lib/announcements';
import { CircleTutorial } from '@/components/CircleTutorial';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Badge } from '@/components/ui/Badge';
import { HeiSearchBar } from '@/components/HeiSearchBar';
import { circleShortName, formatUserId } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { searchProfiles, type SearchProfileResult } from '@/lib/profiles';
import type { Profile } from '@/lib/database.types';
import { findContactsOnHeirapsia, getContactsPermissionStatus, requestContactsPermission } from '@/lib/contactsMatch';

const GOLD = '#C9A843';

const TUTORIAL_KEY = 'circle_tutorial_shown';

const NODE_R = 26;
const LABEL_Y = NODE_R + 14;
const MIN_ORBIT_R = 95;
const MAX_ORBIT_R = 170;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

type FilterType = 'all' | 'active' | 'pro' | 'recent';

type NodeData = {
  id: string;
  name: string | null;
  nickname: string | null;
  avatarUrl: string | null;
  x: number;
  y: number;
  completionRate: number | null;
  isMe: boolean;
  level: number;
  portfolioEnabled: boolean;
  hasActiveAgreement: boolean;
  connectedAt: string;
};

function initials(n: string | null): string {
  if (!n?.trim()) return '?';
  const p = n.trim().split(/\s+/);
  return p.length >= 2 ? `${p[0][0]}${p[1][0]}`.toUpperCase() : p[0].slice(0, 2).toUpperCase();
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return i18n.t('circle.time_justNow');
  if (m < 60) return i18n.t('circle.time_minutesAgo', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return i18n.t('circle.time_hoursAgo', { count: h });
  const d = Math.floor(h / 24);
  return i18n.t('circle.time_daysAgo', { count: d });
}

export default function CircleScreen() {
  const router = useRouter();
  const { tab: tabParam } = useLocalSearchParams<{ tab?: string }>();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const { profile: myProfileFull } = useProfile();
  const { refresh: refreshBadge } = useNeedsAction();
  const { width, height } = useWindowDimensions();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<'contacts' | 'feed'>('contacts');
  const [connections, setConnections] = useState<ConnectionWithProfile[]>([]);
  const [pending, setPending] = useState<PendingConnection[]>([]);
  const [myProfile, setMyProfile] = useState<{ full_name: string | null; avatar_url: string | null } | null>(null);
  const [completionRates, setCompletionRates] = useState<Record<string, number | null>>({});
  const [mutualPairs, setMutualPairs] = useState<MutualPair[]>([]);
  const [activePartnerIds, setActivePartnerIds] = useState<Set<string>>(new Set());
  const [announcements, setAnnouncements] = useState<Map<string, Announcement>>(new Map());
  const [announcementsList, setAnnouncementsList] = useState<Announcement[]>([]);
  const [highlightAnnouncementId, setHighlightAnnouncementId] = useState<string | null>(null);
  const feedListRef = useRef<FlatList<Announcement>>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [showPending, setShowPending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchProfileResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [contactsPermGranted, setContactsPermGranted] = useState(false);
  const [outsideCircleCount, setOutsideCircleCount] = useState<number | null>(null);
  const [showContactsExplainer, setShowContactsExplainer] = useState(false);

  useEffect(() => {
    if (tabParam === 'feed') setActiveTab('feed');
  }, [tabParam]);

  useEffect(() => {
    void getContactsPermissionStatus().then((status) => {
      if (status !== 'granted') return;
      setContactsPermGranted(true);
      void findContactsOnHeirapsia()
        .then((matches) => setOutsideCircleCount(matches.filter((m) => m.connection_status !== 'accepted').length))
        .catch(() => {});
    }).catch(() => {});
  }, []);

  /** Πρώτο πάτημα: αν η άδεια δεν έχει ήδη δοθεί, δείξε ΠΡΩΤΑ το δικό μας explainer
   * modal (Google Play policy για sensitive permissions) — το OS dialog έρχεται
   * μετά, μόνο αν ο χρήστης πατήσει "Συμφωνώ". */
  function onContactsBannerPress() {
    if (contactsPermGranted) {
      router.push('/(app)/contacts-matches');
      return;
    }
    setShowContactsExplainer(true);
  }

  async function confirmContactsAccess() {
    setShowContactsExplainer(false);
    const granted = await requestContactsPermission().catch(() => false);
    if (!granted) {
      appAlert('', t('contactsMatch.permissionDenied'));
      return;
    }
    setContactsPermGranted(true);
    const matches = await findContactsOnHeirapsia().catch(() => []);
    setOutsideCircleCount(matches.filter((m) => m.connection_status !== 'accepted').length);
    router.push('/(app)/contacts-matches');
  }

  const panRef = useRef({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  const lastPanOrigin = useRef({ x: 0, y: 0 });
  const lastPinchDist = useRef<number | null>(null);
  const didPan = useRef(false);
  const nodesRef = useRef<NodeData[]>([]);
  const cxRef = useRef(0);
  const cyRef = useRef(0);
  const graphOffsetY = useRef(0);
  const onNodePressRef = useRef<(id: string) => void>(() => {});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [conns, pend] = await Promise.all([listMyConnections(), listPendingReceived()]);
      setConnections(conns);
      setPending(pend);

      if (user?.id) {
        const [mpRes, activeIds, annList] = await Promise.all([
          supabase.from('profiles').select('full_name, avatar_url').eq('id', user.id).single(),
          getActiveAgreementPartnerIds(user.id),
          getCircleAnnouncements().catch(() => []),
        ]);
        setMyProfile(mpRes.data ?? null);
        setActivePartnerIds(activeIds);
        setAnnouncementsList(annList);
        const annMap = new Map<string, Announcement>();
        for (const a of annList) annMap.set(a.creator_id, a);
        setAnnouncements(annMap);
        if (annList.length > 0) {
          const latest = annList.reduce((max, a) => (a.created_at > max ? a.created_at : max), annList[0].created_at);
          void AsyncStorage.setItem('announcements_last_seen', latest);
        }
      }

      if (conns.length > 0) {
        const ids = conns.map((c) => c.other_id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: rates } = await (supabase.rpc as any)('get_completion_rates', { p_user_ids: ids });
        const rateMap: Record<string, number | null> = {};
        for (const r of (rates ?? []) as { user_id: string; completion_rate: number | null }[]) {
          rateMap[r.user_id] = r.completion_rate;
        }
        setCompletionRates(rateMap);

        const pairs = await getMutualConnections();
        setMutualPairs(pairs);
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useFocusEffect(useCallback(() => {
    setLoadError(false);
    void load();
    void markNotificationsReadForRoute('/(app)/circle');
  }, [load]));

  useEffect(() => {
    void AsyncStorage.getItem(TUTORIAL_KEY).then((v) => {
      if (!v) setShowTutorial(true);
    });
  }, []);

  function dismissTutorial() {
    void AsyncStorage.setItem(TUTORIAL_KEY, '1');
    setShowTutorial(false);
  }

  const now = useMemo(() => Date.now(), []);

  const allNodes = useMemo<NodeData[]>(() => {
    const result: NodeData[] = [{
      id: user?.id ?? 'me',
      name: myProfile?.full_name ?? null,
      nickname: null,
      avatarUrl: myProfile?.avatar_url ?? null,
      x: 0, y: 0,
      completionRate: null,
      isMe: true,
      level: 0,
      portfolioEnabled: false,
      hasActiveAgreement: false,
      connectedAt: '',
    }];

    if (connections.length === 0) return result;

    const orbitR = Math.min(MAX_ORBIT_R, Math.max(MIN_ORBIT_R, connections.length * 20));
    const step = (2 * Math.PI) / connections.length;

    connections.forEach((c, i) => {
      const angle = i * step - Math.PI / 2;
      result.push({
        id: c.other_id,
        name: c.full_name,
        nickname: c.nickname ?? null,
        avatarUrl: c.avatar_url,
        x: Math.cos(angle) * orbitR,
        y: Math.sin(angle) * orbitR,
        completionRate: completionRates[c.other_id] ?? null,
        isMe: false,
        level: c.current_level ?? 1,
        portfolioEnabled: c.portfolio_enabled ?? false,
        hasActiveAgreement: activePartnerIds.has(c.other_id),
        connectedAt: c.connected_at,
      });
    });

    return result;
  }, [connections, myProfile, completionRates, user?.id, activePartnerIds]);

  const nodes = useMemo(() => {
    if (activeFilter === 'all') return allNodes;
    return allNodes.filter((n) => {
      if (n.isMe) return true;
      if (activeFilter === 'active') return n.hasActiveAgreement;
      if (activeFilter === 'pro') return n.portfolioEnabled;
      if (activeFilter === 'recent') return now - new Date(n.connectedAt).getTime() <= THIRTY_DAYS_MS;
      return true;
    });
  }, [allNodes, activeFilter, now]);

  const nodeMap = useMemo(() => {
    const m: Record<string, NodeData> = {};
    for (const n of allNodes) m[n.id] = n;
    return m;
  }, [allNodes]);

  const visibleSet = useMemo(() => new Set(nodes.map((n) => n.id)), [nodes]);

  const circleIds = useMemo(() => new Set(connections.map((c) => c.other_id)), [connections]);

  // Unified αναζήτηση με searchProfiles (φωνητική, transliteration, fuzzy)
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); setSearchLoading(false); return; }
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await searchProfiles(q, user?.id);
        // In-circle πρώτα, μετά out-of-circle
        const inCircle = res.filter((p) => circleIds.has(p.id));
        const outside = res.filter((p) => !circleIds.has(p.id));
        setSearchResults([...inCircle, ...outside]);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [searchQuery, circleIds, user?.id]);

  // Highlighted nodes στο γράφο = in-circle αποτελέσματα
  const highlightedIds = useMemo(() => {
    if (!searchQuery.trim()) return new Set<string>();
    return new Set(searchResults.filter((p) => circleIds.has(p.id)).map((p) => p.id));
  }, [searchResults, searchQuery, circleIds]);

  const HEADER_H = insets.top + 56;
  const FILTER_H = connections.length > 0 ? 44 : 0;
  const SEARCH_H = connections.length > 0 ? 52 : 0;
  const graphHeight = height - HEADER_H - FILTER_H - SEARCH_H;

  const cx = width / 2;
  const cy = graphHeight / 2;

  // Refs για χρήση μέσα στον PanResponder (δημιουργείται μία φορά)
  cxRef.current = cx;
  cyRef.current = cy;
  nodesRef.current = nodes;
  onNodePressRef.current = onNodePress;

  const tapStartPos = useRef<{ x: number; y: number } | null>(null);

  const panResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: (_, gs) => {
      const moved = Math.abs(gs.dx) > 6 || Math.abs(gs.dy) > 6;
      if (moved) didPan.current = true;
      return moved;
    },
    onPanResponderGrant: (evt) => {
      lastPanOrigin.current = { ...panRef.current };
      lastPinchDist.current = null;
      didPan.current = false;
      const t = evt.nativeEvent.touches[0];
      tapStartPos.current = t ? { x: t.pageX, y: t.pageY } : null;
    },
    onPanResponderMove: (evt, gs) => {
      const touches = evt.nativeEvent.touches;
      if (touches.length >= 2) {
        const dx = touches[0].pageX - touches[1].pageX;
        const dy = touches[0].pageY - touches[1].pageY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (lastPinchDist.current !== null) {
          const ratio = dist / lastPinchDist.current;
          const ns = Math.max(0.35, Math.min(3.5, scaleRef.current * ratio));
          scaleRef.current = ns;
          setScale(ns);
        }
        lastPinchDist.current = dist;
      } else {
        lastPinchDist.current = null;
        const nx = lastPanOrigin.current.x + gs.dx;
        const ny = lastPanOrigin.current.y + gs.dy;
        panRef.current = { x: nx, y: ny };
        setPan({ x: nx, y: ny });
      }
    },
    onPanResponderRelease: (evt) => {
      lastPinchDist.current = null;
      if (didPan.current) { didPan.current = false; return; }

      // Ανίχνευση tap: βρες αν η αφή εμπίπτει σε κάποιον κόμβο
      const touch = evt.nativeEvent.changedTouches[0];
      if (!touch) { setSelectedId(null); return; }

      const tapX = touch.pageX;
      // pageY είναι από την κορυφή της οθόνης — αφαιρούμε το Y offset του graphWrap
      const tapY = touch.pageY - graphOffsetY.current;

      const hitRadius = (NODE_R + 12) * scaleRef.current;
      let hitNode: string | null = null;

      for (const n of nodesRef.current) {
        if (n.isMe) continue;
        const screenX = n.x * scaleRef.current + cxRef.current + panRef.current.x;
        const screenY = n.y * scaleRef.current + cyRef.current + panRef.current.y;
        const dist = Math.sqrt((tapX - screenX) ** 2 + (tapY - screenY) ** 2);
        if (dist <= hitRadius) { hitNode = n.id; break; }
      }

      if (hitNode) {
        onNodePressRef.current(hitNode);
      } else {
        setSelectedId(null);
      }
    },
  }), []);

  async function onRespond(requesterId: string, accept: boolean) {
    setBusy(requesterId);
    try {
      await respondConnectionRequest(requesterId, accept);
      setPending((prev) => prev.filter((c) => c.requester_id !== requesterId));
      if (accept) {
        const fresh = await listMyConnections();
        setConnections(fresh);
        const pairs = await getMutualConnections();
        setMutualPairs(pairs);
      }
      refreshBadge();
      if (pending.length <= 1) setShowPending(false);
    } catch {
      appAlert(t('common.error'), t('user.error_response'));
    } finally {
      setBusy(null);
    }
  }

  function onNodePress(nodeId: string) {
    if (selectedId === nodeId) {
      router.push({ pathname: '/(app)/users/[id]', params: { id: nodeId } });
      setSelectedId(null);
    } else {
      setSelectedId(nodeId);
    }
  }

  const selectedNode = selectedId ? nodeMap[selectedId] : null;
  const tooltipPos = selectedNode ? {
    x: selectedNode.x * scale + cx + pan.x,
    y: selectedNode.y * scale + cy + pan.y,
  } : null;

  const FILTER_TABS: { key: FilterType; label: string }[] = [
    { key: 'all', label: t('circle.filter_all') },
    { key: 'active', label: t('circle.filter_activeAgreement') },
    { key: 'pro', label: t('circle.filter_professionals') },
    { key: 'recent', label: t('circle.filter_recent') },
  ];

  if (loading || loadError) {
    return (
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <View style={styles.headerRow}>
          <Pressable onPress={() => router.back()} hitSlop={12} style={styles.backBtn}>
            <Ionicons name="arrow-back" size={22} color={colors.primaryLight} />
          </Pressable>
          <Text style={styles.title}>{t('circle.my_circle')}</Text>
        </View>
        <View style={styles.center}>
          {loadError ? (
            <>
              <Ionicons name="cloud-offline-outline" size={44} color={colors.border} />
              <Text style={styles.emptyTitle}>{t('common.loadFailed')}</Text>
              <Pressable style={styles.findBtn} onPress={() => { setLoadError(false); void load(); }} accessibilityRole="button">
                <Text style={styles.findBtnText}>{t('circle.retry')}</Text>
              </Pressable>
            </>
          ) : (
            <ActivityIndicator size="large" color={colors.primary} />
          )}
        </View>
      </View>
    );
  }

  const isEmpty = connections.length === 0 && pending.length === 0;

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      {/* Header */}
      <View style={styles.headerRow}>
        <Pressable onPress={() => router.back()} hitSlop={12} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={22} color={colors.primaryLight} />
        </Pressable>
        <Text style={styles.title}>{t('circle.my_circle')}</Text>
        <View style={styles.headerRight}>
          {connections.length > 0 && (
            <Text style={styles.countLabel}>{t('circle.connectionCount', { count: connections.length })}</Text>
          )}
          {/* Incoming requests badge button */}
          <Pressable
            style={styles.pendingIconWrap}
            onPress={() => pending.length > 0 && setShowPending(true)}
            hitSlop={12}
            accessibilityRole="button"
            accessibilityLabel={t('circle.connectionRequests', { count: pending.length })}
          >
            <Ionicons name="person-add-outline" size={22} color={pending.length > 0 ? colors.warning : colors.textMuted} />
            <Badge count={pending.length} pulse style={styles.requestBadge} />
          </Pressable>
          {myProfileFull?.portfolio_enabled && (
            <Pressable
              style={styles.announceBtnWrap}
              onPress={() => router.push('/(app)/announcements/new')}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={t('circle.newAnnouncement')}
            >
              <Ionicons name="megaphone-outline" size={19} color="#0D1B2A" />
              <Text style={styles.announceBtnText} numberOfLines={2}>{t('circle.announcement')}</Text>
            </Pressable>
          )}
          <Pressable onPress={() => setShowTutorial(true)} hitSlop={12} accessibilityRole="button" accessibilityLabel={t('common.help')}>
            <Ionicons name="help-circle-outline" size={22} color={colors.textMuted} />
          </Pressable>
        </View>
      </View>

      {/* Επαφές εκτός κύκλου / discovery prompt */}
      {(!contactsPermGranted || (!!outsideCircleCount && outsideCircleCount > 0)) && (
        <Pressable style={styles.outsideCircleBanner} onPress={onContactsBannerPress} accessibilityRole="button">
          <Ionicons name="people-circle-outline" size={16} color={GOLD} />
          <Text style={styles.outsideCircleBannerText}>
            {contactsPermGranted
              ? t('contactsMatch.outsideCircleBanner', { count: outsideCircleCount })
              : t('contactsMatch.circleDiscoverBanner')}
          </Text>
          <Ionicons name="chevron-forward" size={14} color={GOLD} />
        </Pressable>
      )}

      <ConfirmModal
        visible={showContactsExplainer}
        title={t('contactsMatch.explainerTitle')}
        message={t('contactsMatch.explainerIntro')}
        bullets={[
          t('contactsMatch.explainerWhat'),
          t('contactsMatch.explainerWhy'),
          t('contactsMatch.explainerHow'),
        ]}
        confirmLabel={t('contactsMatch.explainerAgree')}
        cancelLabel={t('contactsMatch.explainerDecline')}
        onConfirm={() => void confirmContactsAccess()}
        onCancel={() => setShowContactsExplainer(false)}
      />

      {/* Tab bar */}
      <View style={styles.tabBar}>
        <Pressable
          style={[styles.tabItem, activeTab === 'contacts' && styles.tabItemActive]}
          onPress={() => setActiveTab('contacts')}
          accessibilityRole="tab"
        >
          <Ionicons name="people-outline" size={15} color={activeTab === 'contacts' ? colors.primary : colors.textMuted} />
          <Text style={[styles.tabLabel, activeTab === 'contacts' && styles.tabLabelActive]}>{t('circle.tab_contacts')}</Text>
        </Pressable>
        <Pressable
          style={[styles.tabItem, activeTab === 'feed' && styles.tabItemActive]}
          onPress={() => setActiveTab('feed')}
          accessibilityRole="tab"
        >
          <Ionicons name="megaphone-outline" size={15} color={activeTab === 'feed' ? colors.primary : colors.textMuted} />
          <Text style={[styles.tabLabel, activeTab === 'feed' && styles.tabLabelActive]}>{t('circle.tab_feed')}</Text>
          {announcementsList.length > 0 && activeTab !== 'feed' && (
            <View style={styles.tabDot} />
          )}
        </Pressable>
      </View>

      {/* Feed Tab */}
      {activeTab === 'feed' && (
        announcementsList.length === 0 ? (
          <View style={styles.feedEmpty}>
            <Ionicons name="megaphone-outline" size={40} color={colors.border} />
            <Text style={styles.feedEmptyText}>
              {loading ? t('common.loading') : t('circle.feed_empty')}
            </Text>
          </View>
        ) : (
          <FlatList
            ref={feedListRef}
            style={styles.tabFlatList}
            data={announcementsList}
            keyExtractor={(a) => a.id}
            contentContainerStyle={styles.feedList}
            showsVerticalScrollIndicator={false}
            onScrollToIndexFailed={() => {}}
            renderItem={({ item: a }) => {
              const logoUri = a.creator_use_logo && a.creator_business_logo
                ? a.creator_business_logo
                : a.creator_avatar;
              return (
                <View style={[styles.feedCard, highlightAnnouncementId === a.id && styles.feedCardHighlight]}>
                  <View style={styles.feedCardHeader}>
                    {logoUri ? (
                      <Image source={{ uri: logoUri }} style={styles.feedAvatar} />
                    ) : (
                      <View style={[styles.feedAvatar, styles.feedAvatarFallback]}>
                        <Text style={styles.feedAvatarLetter}>
                          {a.creator_name?.[0]?.toUpperCase() ?? '?'}
                        </Text>
                      </View>
                    )}
                    <View style={styles.feedMeta}>
                      <Text style={styles.feedName} numberOfLines={1}>{a.creator_name ?? '—'}</Text>
                      <Text style={styles.feedTime}>{timeAgo(a.created_at)}</Text>
                    </View>
                    <Pressable
                      hitSlop={8}
                      onPress={() => router.push({ pathname: '/(app)/users/[id]', params: { id: a.creator_id } })}
                      accessibilityRole="button"
                    >
                      <Ionicons name="person-circle-outline" size={22} color={colors.textMuted} />
                    </Pressable>
                  </View>
                  <Text style={styles.feedText}>{a.text}</Text>
                  {!!a.image_url && (
                    <Image source={{ uri: a.image_url }} style={styles.feedImage} resizeMode="cover" />
                  )}
                </View>
              );
            }}
          />
        )
      )}

      {/* Filter bar — only in contacts tab */}
      {activeTab === 'contacts' && connections.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.filterBar}
          contentContainerStyle={styles.filterBarContent}
        >
          {FILTER_TABS.map((tab) => (
            <Pressable
              key={tab.key}
              style={[styles.filterPill, activeFilter === tab.key && styles.filterPillActive]}
              onPress={() => setActiveFilter(tab.key)}
              accessibilityRole="button"
            >
              <Text style={[styles.filterPillText, activeFilter === tab.key && styles.filterPillTextActive]}>
                {tab.label}
              </Text>
            </Pressable>
          ))}
        </ScrollView>
      )}

      {/* Search bar + graph — contacts tab only */}
      {activeTab === 'contacts' && connections.length > 0 && (
        <View style={styles.circleSearchWrap}>
          <HeiSearchBar onQueryChange={setSearchQuery} />
        </View>
      )}

      {activeTab === 'contacts' && (isEmpty ? (
        <View style={styles.center}>
          <Ionicons name="people-outline" size={52} color={colors.border} />
          <Text style={styles.emptyTitle}>{t('circle.empty_title')}</Text>
          <Text style={styles.emptyText}>{t('circle.empty_body')}</Text>
          <Pressable style={styles.findBtn} onPress={() => router.push('/(app)/users')} accessibilityRole="button">
            <Ionicons name="search" size={16} color="#fff" />
            <Text style={styles.findBtnText}>{t('circle.action_findUsers')}</Text>
          </Pressable>
        </View>
      ) : (
        <View
        style={[styles.graphWrap, { height: graphHeight }]}
        onLayout={(e) => { graphOffsetY.current = e.nativeEvent.layout.y; }}
        {...panResponder.panHandlers}
      >
          <Svg width={width} height={graphHeight} style={styles.svg}>
            <Defs>
              {allNodes.map((n) => (
                <ClipPath key={`c-${n.id}`} id={`c-${n.id}`}>
                  <Circle cx={0} cy={0} r={NODE_R} />
                </ClipPath>
              ))}
            </Defs>

            {/* Background rect — touch handling γίνεται από PanResponder */}
            <Rect x={0} y={0} width={width} height={graphHeight} fill="transparent" />

            <G transform={`translate(${cx + pan.x}, ${cy + pan.y}) scale(${scale})`}>
              {/* Lines: me → each visible connection */}
              {nodes.filter((n) => !n.isMe).map((n) => (
                <Line
                  key={`lm-${n.id}`}
                  x1={0} y1={0} x2={n.x} y2={n.y}
                  stroke={colors.borderStrong}
                  strokeWidth={2 / scale}
                  opacity={0.7}
                />
              ))}

              {/* Lines: mutual pairs (dashed) — only for visible nodes */}
              {mutualPairs.map((pair, i) => {
                const a = nodeMap[pair.user_a];
                const b = nodeMap[pair.user_b];
                if (!a || !b) return null;
                if (!visibleSet.has(a.id) || !visibleSet.has(b.id)) return null;
                return (
                  <Line
                    key={`mp-${i}`}
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={colors.primaryLight}
                    strokeWidth={2 / scale}
                    strokeDasharray={`${6 / scale},${4 / scale}`}
                    opacity={0.65}
                  />
                );
              })}

              {/* Nodes */}
              {nodes.map((n) => {
                const selected = selectedId === n.id;
                const highlighted = highlightedIds.has(n.id);
                const dimmed = searchQuery.trim() !== '' && !n.isMe && !highlighted;
                const ini = initials(n.name);

                return (
                  <G
                    key={n.id}
                    transform={`translate(${n.x}, ${n.y})`}
                    opacity={dimmed ? 0.25 : 1}
                    onPress={undefined}
                  >
                    {selected && (
                      <Circle cx={0} cy={0} r={NODE_R + 5} fill="none" stroke={colors.primary} strokeWidth={2.5} />
                    )}
                    {highlighted && !selected && (
                      <Circle cx={0} cy={0} r={NODE_R + 5} fill="none" stroke={colors.warning} strokeWidth={2} />
                    )}
                    {n.isMe && (
                      <Circle cx={0} cy={0} r={NODE_R + 4} fill="none" stroke={colors.primary} strokeWidth={1.5} opacity={0.5} />
                    )}

                    <Circle
                      cx={0} cy={0} r={NODE_R}
                      fill={n.isMe ? colors.primary : colors.surface}
                      stroke={n.isMe ? colors.primary : colors.border}
                      strokeWidth={1.5}
                    />

                    {n.avatarUrl ? (
                      <SvgImage
                        x={-NODE_R} y={-NODE_R}
                        width={NODE_R * 2} height={NODE_R * 2}
                        href={{ uri: n.avatarUrl }}
                        clipPath={`url(#c-${n.id})`}
                        preserveAspectRatio="xMidYMid slice"
                      />
                    ) : (
                      <SvgText
                        x={0} y={5}
                        fontSize={13}
                        fontWeight="700"
                        fill={n.isMe ? '#fff' : colors.text}
                        textAnchor="middle"
                      >
                        {ini}
                      </SvgText>
                    )}

                    {/* Active agreement handshake — top right */}
                    {!n.isMe && n.hasActiveAgreement && (
                      <G transform={`translate(${NODE_R - 2}, ${-NODE_R + 2})`}>
                        <Rect x={-9} y={-9} width={18} height={13} rx={4} fill="#10B981" opacity={0.92} />
                        <SvgText x={0} y={3} fontSize={9} fill="#fff" textAnchor="middle">🤝</SvgText>
                      </G>
                    )}

                    {/* Announcement chat bubble — top left */}
                    {!n.isMe && announcements.has(n.id) && (
                      <G transform={`translate(${-NODE_R + 4}, ${-NODE_R + 4})`}>
                        <Circle r={7} fill="#3B82F6" />
                        <SvgText x={0} y={3} fontSize={8} fill="#fff" textAnchor="middle">💬</SvgText>
                      </G>
                    )}

                    {/* Portfolio badge — bottom left */}
                    {!n.isMe && n.portfolioEnabled && (
                      <G transform={`translate(${-NODE_R + 2}, ${NODE_R - 2})`}>
                        <Rect x={-9} y={-9} width={18} height={13} rx={4} fill={colors.primary} opacity={0.92} />
                        <SvgText x={0} y={3} fontSize={9} fill="#0D1B2A" textAnchor="middle">💼</SvgText>
                      </G>
                    )}

                    {/* Level badge — bottom right */}
                    {!n.isMe && n.level > 0 && (
                      <G transform={`translate(${NODE_R - 2}, ${NODE_R - 2})`}>
                        <Rect x={-14} y={-9} width={16} height={12} rx={4} fill="#1E293B" opacity={0.85} />
                        <SvgText x={-6} y={4} fontSize={8} fontWeight="700" fill="#94A3B8" textAnchor="middle">
                          {`L${n.level}`}
                        </SvgText>
                      </G>
                    )}

                    <SvgText
                      x={0} y={LABEL_Y}
                      fontSize={10}
                      fill={highlighted ? colors.warning : colors.textMuted}
                      textAnchor="middle"
                    >
                      {n.isMe ? t('circle.me') : circleShortName(n.name, n.nickname)}
                    </SvgText>

                  </G>
                );
              })}
            </G>
          </Svg>

          {/* Network stats */}
          {connections.length > 0 && (
            <View style={styles.statsOverlay}>
              <Text style={styles.statsText}>
                {t('circle.stats_connections', { count: connections.length })}
                {mutualPairs.length > 0 ? t('circle.stats_mutual', { count: mutualPairs.length }) : ''}
              </Text>
            </View>
          )}

          {/* Tooltip */}
          {selectedNode && tooltipPos && (() => {
            const tooltipW = 272;
            const tooltipLeft = Math.max(8, Math.min(width - tooltipW, tooltipPos.x - tooltipW / 2));
            const tooltipTop = Math.max(8, tooltipPos.y - NODE_R * scale - 160);
            // Πόσο δεξιά από το αριστερό άκρο του tooltip βρίσκεται ο κόμβος
            const arrowLeft = Math.max(16, Math.min(tooltipW - 16, tooltipPos.x - tooltipLeft));
            return (
            <View style={{ position: 'absolute', left: tooltipLeft, top: tooltipTop }} pointerEvents="box-none">
              <View style={[styles.tooltip]}>

              <View style={styles.tooltipNameRow}>
                <Text style={styles.tooltipName} numberOfLines={1}>{selectedNode.name?.trim() || t('common.user')}</Text>
                {selectedNode.completionRate !== null && (
                  <View style={styles.tooltipScore}>
                    <Text style={styles.tooltipScoreText}>✓ {selectedNode.completionRate}%</Text>
                  </View>
                )}
              </View>
              {(selectedNode.portfolioEnabled || selectedNode.hasActiveAgreement) && (
                <View style={styles.tooltipBadgeRow}>
                  {selectedNode.portfolioEnabled && (
                    <View style={styles.tooltipBadge}>
                      <Ionicons name="briefcase-outline" size={11} color={colors.primary} />
                      <Text style={styles.tooltipBadgeText}>{t('circle.badge_professional')}</Text>
                    </View>
                  )}
                  {selectedNode.hasActiveAgreement && (
                    <View style={[styles.tooltipBadge, styles.tooltipBadgeGreen]}>
                      <Ionicons name="hand-left-outline" size={11} color="#10B981" />
                      <Text style={[styles.tooltipBadgeText, styles.tooltipBadgeTextGreen]}>{t('circle.filter_activeAgreement')}</Text>
                    </View>
                  )}
                </View>
              )}
              {announcements.has(selectedNode.id) && (
                <>
                  <View style={styles.tooltipAnnouncement}>
                    <Ionicons name="megaphone-outline" size={12} color="#3B82F6" />
                    <Text style={styles.tooltipAnnouncementText}>
                      {announcements.get(selectedNode.id)!.text}
                    </Text>
                  </View>
                  <Pressable
                    style={styles.tooltipBtnOutline}
                    onPress={() => {
                      const ann = announcements.get(selectedNode.id)!;
                      setSelectedId(null);
                      setActiveTab('feed');
                      setHighlightAnnouncementId(ann.id);
                      setTimeout(() => {
                        const idx = announcementsList.findIndex((a) => a.id === ann.id);
                        if (idx >= 0) {
                          try { feedListRef.current?.scrollToIndex({ index: idx, animated: true, viewPosition: 0.2 }); } catch { /* ignore */ }
                        }
                      }, 200);
                      setTimeout(() => setHighlightAnnouncementId(null), 3000);
                    }}
                    accessibilityRole="button"
                  >
                    <Ionicons name="megaphone-outline" size={12} color={colors.primaryLight} />
                    <Text style={styles.tooltipBtnTextMuted}>{t('circle.viewAnnouncement')}</Text>
                  </Pressable>
                </>
              )}
              <View style={styles.tooltipActions}>
                <Pressable
                  style={styles.tooltipBtnPrimary}
                  onPress={() => {
                    setSelectedId(null);
                    router.push({ pathname: '/(app)/users/[id]', params: { id: selectedNode.id } });
                  }}
                  accessibilityRole="button"
                >
                  <Text style={styles.tooltipBtnTextWhite}>{t('common.view')}</Text>
                </Pressable>
                <Pressable
                  style={styles.tooltipBtnOutline}
                  onPress={() => {
                    setSelectedId(null);
                    router.push({
                      pathname: '/(app)/agreements/new',
                      params: { counterpartyId: selectedNode.id, counterpartyName: selectedNode.name ?? '' },
                    });
                  }}
                  accessibilityRole="button"
                >
                  <Text style={styles.tooltipBtnTextMuted}>{t('circle.action_newAgreement')}</Text>
                </Pressable>
              </View>
              {myProfileFull?.portfolio_enabled && (
                <Pressable
                  style={styles.tooltipBtnPortfolio}
                  onPress={() => {
                    setSelectedId(null);
                    router.push({
                      pathname: '/(app)/offers/new',
                      params: { recipientId: selectedNode.id, recipientName: selectedNode.name ?? '' },
                    });
                  }}
                  accessibilityRole="button"
                >
                  <Ionicons name="briefcase-outline" size={12} color={colors.primaryLight} />
                  <Text style={styles.tooltipBtnTextMuted}>{t('circle.action_sendOffer')}</Text>
                </Pressable>
              )}
              </View>
              {/* Μύτη κάτω από το card, δείχνει προς το node */}
              <View style={{ height: 9 }}>
                <View style={[styles.tooltipArrowBorder, { position: 'absolute', top: 0, left: arrowLeft - 9 }]} />
                <View style={[styles.tooltipArrowFill, { position: 'absolute', top: -1, left: arrowLeft - 8 }]} />
              </View>
            </View>
            );
          })()}

          {/* Zoom buttons */}
          <View style={[styles.zoomControls, { bottom: insets.bottom + 16 }]}>
            <Pressable
              style={styles.zoomBtn}
              onPress={() => { const ns = Math.min(3.5, scaleRef.current * 1.3); scaleRef.current = ns; setScale(ns); }}
              accessibilityRole="button"
              accessibilityLabel={t('circle.zoomIn')}
            >
              <Ionicons name="add" size={20} color={colors.text} />
            </Pressable>
            <Pressable
              style={styles.zoomBtn}
              onPress={() => { const ns = Math.max(0.35, scaleRef.current / 1.3); scaleRef.current = ns; setScale(ns); }}
              accessibilityRole="button"
              accessibilityLabel={t('circle.zoomOut')}
            >
              <Ionicons name="remove" size={20} color={colors.text} />
            </Pressable>
          </View>
        </View>
      ))}

      <CircleTutorial visible={showTutorial} onDone={dismissTutorial} />

      {/* Pending requests modal */}
      <Modal visible={showPending} transparent animationType="slide" onRequestClose={() => setShowPending(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setShowPending(false)}>
          <Pressable style={[styles.pendingSheet, { paddingBottom: insets.bottom + spacing.md }]} onPress={() => {}}>
            <View style={styles.sheetHandle} />
            <Text style={styles.sheetTitle}>{t('circle.connectionRequests', { count: pending.length })}</Text>
            {pending.map((c) => (
              <View key={c.connection_id} style={styles.pendingRow}>
                <Pressable
                  style={styles.pendingInfo}
                  onPress={() => {
                    setShowPending(false);
                    router.push({ pathname: '/(app)/users/[id]', params: { id: c.requester_id } });
                  }}
                  accessibilityRole="button"
                >
                  <View style={styles.pendingAvatar}>
                    <Text style={styles.pendingAvatarText}>{initials(c.full_name)}</Text>
                  </View>
                  <View style={styles.pendingMeta}>
                    <Text style={styles.pendingName} numberOfLines={1}>{c.full_name?.trim() || t('common.user')}</Text>
                    {c.user_number != null && (
                      <Text style={styles.pendingUserId}>{formatUserId(c.user_number)}</Text>
                    )}
                  </View>
                </Pressable>
                <View style={styles.respondBtns}>
                  <Pressable
                    style={[styles.iconBtn, styles.acceptBtn]}
                    onPress={() => onRespond(c.requester_id, true)}
                    disabled={busy === c.requester_id}
                    accessibilityRole="button"
                    accessibilityLabel={t('common.accept')}
                  >
                    <Ionicons name="checkmark" size={17} color="#fff" />
                  </Pressable>
                  <Pressable
                    style={[styles.iconBtn, styles.declineBtn]}
                    onPress={() => onRespond(c.requester_id, false)}
                    disabled={busy === c.requester_id}
                    accessibilityRole="button"
                    accessibilityLabel={t('common.reject')}
                  >
                    <Ionicons name="close" size={17} color={colors.textMuted} />
                  </Pressable>
                </View>
              </View>
            ))}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
      minHeight: 56,
    },
    backBtn: { marginRight: spacing.sm },
    title: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    headerRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    announceBtnWrap: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      minWidth: 74,
      backgroundColor: '#C9A843',
      borderRadius: radius.md,
      paddingVertical: 8,
      paddingHorizontal: 10,
    },
    announceBtnText: {
      color: '#0D1B2A',
      fontSize: 11,
      fontWeight: '700',
      includeFontPadding: false,
      textAlign: 'center',
      lineHeight: 14,
      flexShrink: 1,
    },
    pendingIconWrap: {
      position: 'relative',
      width: 30,
      height: 30,
      alignItems: 'center',
      justifyContent: 'center',
    },
    requestBadge: {
      position: 'absolute',
      top: -6,
      right: -8,
    },
    countLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '500',
    },
    filterBar: {
      maxHeight: 44,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    filterBarContent: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.xs,
      gap: spacing.xs,
    },
    filterPill: {
      paddingHorizontal: spacing.md,
      paddingVertical: 6,
      borderRadius: radius.full,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    filterPillActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    filterPillText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    filterPillTextActive: {
      color: '#fff',
    },
    circleSearchWrap: {
      marginHorizontal: spacing.lg,
      marginTop: spacing.sm,
      marginBottom: spacing.xs,
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      paddingHorizontal: spacing.xl,
    },
    emptyTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
      textAlign: 'center',
      marginTop: spacing.sm,
    },
    emptyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      lineHeight: 22,
    },
    findBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 10,
      paddingHorizontal: spacing.lg,
      marginTop: spacing.sm,
    },
    findBtnText: {
      color: '#fff',
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    graphWrap: {
      flex: 1,
      overflow: 'hidden',
    },
    svg: {
      position: 'absolute',
      top: 0,
      left: 0,
    },
    statsOverlay: {
      position: 'absolute',
      bottom: 16,
      left: spacing.lg,
      right: 80,
      pointerEvents: 'none',
    },
    statsText: {
      color: colors.textMuted,
      fontSize: 11,
      fontWeight: '500',
    },
    tooltip: {
      width: 272,
      backgroundColor: colors.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 8,
    },
    tooltipNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      flexWrap: 'wrap',
    },
    tooltipName: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
      flexShrink: 1,
    },
    tooltipScore: {
      backgroundColor: 'rgba(74,222,128,0.12)',
      borderRadius: radius.full,
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderWidth: 1,
      borderColor: 'rgba(74,222,128,0.3)',
    },
    tooltipScoreText: {
      color: colors.success,
      fontSize: 11,
      fontWeight: '700',
    },
    tooltipBadgeRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 4,
    },
    tooltipBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      backgroundColor: `${colors.primary}18`,
      borderRadius: radius.full,
      paddingHorizontal: 7,
      paddingVertical: 2,
    },
    tooltipBadgeGreen: {
      backgroundColor: 'rgba(16,185,129,0.14)',
    },
    tooltipBadgeText: {
      color: colors.primary,
      fontSize: 10,
      fontWeight: '700',
    },
    tooltipBadgeTextGreen: {
      color: '#10B981',
    },
    tooltipActions: {
      flexDirection: 'row',
      gap: spacing.xs,
      marginTop: 4,
    },
    tooltipBtnPrimary: {
      flex: 1,
      alignItems: 'center',
      backgroundColor: colors.primary,
      borderRadius: radius.sm,
      paddingHorizontal: spacing.sm,
      paddingVertical: 7,
    },
    tooltipBtnOutline: {
      flex: 1,
      alignItems: 'center',
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.sm,
      paddingHorizontal: spacing.sm,
      paddingVertical: 7,
    },
    tooltipAnnouncement: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 4,
      backgroundColor: 'rgba(59,130,246,0.12)',
      borderRadius: radius.sm,
      paddingVertical: 5,
      paddingHorizontal: 7,
    },
    tooltipAnnouncementText: {
      color: '#93C5FD',
      fontSize: 11,
      flex: 1,
      lineHeight: 15,
    },
    tooltipBtnPortfolio: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors.primaryLight,
      borderRadius: radius.sm,
      paddingVertical: 7,
    },
    tooltipArrowBorder: {
      width: 0,
      height: 0,
      borderLeftWidth: 9,
      borderRightWidth: 9,
      borderTopWidth: 9,
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
      borderTopColor: colors.border,
    },
    tooltipArrowFill: {
      width: 0,
      height: 0,
      borderLeftWidth: 8,
      borderRightWidth: 8,
      borderTopWidth: 8,
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
      borderTopColor: colors.surface,
    },
    tooltipBtnTextWhite: {
      color: '#fff',
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    tooltipBtnTextMuted: {
      color: colors.primaryLight,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    zoomControls: {
      position: 'absolute',
      right: spacing.lg,
      gap: spacing.xs,
    },
    zoomBtn: {
      width: 38,
      height: 38,
      borderRadius: 19,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.12,
      shadowRadius: 4,
      elevation: 4,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'flex-end',
    },
    pendingSheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      padding: spacing.lg,
      gap: spacing.sm,
    },
    sheetHandle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: colors.border,
      alignSelf: 'center',
      marginBottom: spacing.xs,
    },
    sheetTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
      marginBottom: spacing.xs,
    },
    pendingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: spacing.sm,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    pendingInfo: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    pendingAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.surfaceAlt,
      alignItems: 'center',
      justifyContent: 'center',
    },
    pendingAvatarText: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    pendingMeta: { flex: 1 },
    pendingName: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    pendingUserId: {
      color: colors.textMuted,
      fontSize: 11,
      marginTop: 1,
    },
    respondBtns: {
      flexDirection: 'row',
      gap: spacing.xs,
      marginLeft: spacing.sm,
    },
    iconBtn: {
      width: 34,
      height: 34,
      borderRadius: 17,
      alignItems: 'center',
      justifyContent: 'center',
    },
    acceptBtn: { backgroundColor: colors.success },
    declineBtn: {
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.border,
    },
    outsideCircleBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      marginHorizontal: spacing.lg,
      marginBottom: spacing.sm,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: `${GOLD}55`,
      backgroundColor: `${GOLD}14`,
    },
    outsideCircleBannerText: {
      flex: 1,
      color: GOLD,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    // ── Tabs ──────────────────────────────────────────────────────────────────
    tabBar: {
      flexDirection: 'row',
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
      backgroundColor: colors.surface,
    },
    tabItem: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
      paddingVertical: 10,
      position: 'relative',
    },
    tabItemActive: {
      borderBottomWidth: 2,
      borderBottomColor: colors.primary,
    },
    tabLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    tabLabelActive: {
      color: colors.primary,
    },
    tabDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: colors.warning,
      position: 'absolute',
      top: 8,
      right: '25%',
    },
    // ── Feed ──────────────────────────────────────────────────────────────────
    tabFlatList: {
      flex: 1,
    },
    feedList: {
      // Σε πλατιά οθόνη το περιεχόμενο μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.wide,
      alignSelf: 'center',
      padding: spacing.md,
      gap: spacing.md,
    },
    feedEmpty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.md,
      padding: spacing.xl,
    },
    feedEmptyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      lineHeight: 22,
    },
    feedCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      padding: spacing.md,
      gap: spacing.sm,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.border,
    },
    feedCardHighlight: {
      borderColor: '#3B82F6',
      borderWidth: 2,
      backgroundColor: 'rgba(59,130,246,0.06)',
    },
    feedCardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    feedAvatar: {
      width: 44,
      height: 44,
      borderRadius: 22,
    },
    feedAvatarFallback: {
      backgroundColor: colors.surfaceAlt,
      alignItems: 'center',
      justifyContent: 'center',
    },
    feedAvatarLetter: {
      color: colors.textMuted,
      fontSize: 18,
      fontWeight: '700',
    },
    feedMeta: {
      flex: 1,
      gap: 2,
    },
    feedName: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    feedTime: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    feedText: {
      color: colors.text,
      fontSize: 16,
      lineHeight: 24,
    },
    feedImage: {
      width: '100%',
      aspectRatio: 16 / 9,
      borderRadius: radius.md,
      backgroundColor: colors.surfaceAlt,
    },
  });
}
